import { ShieldCheck, ShieldAlert, AlertOctagon, AlertTriangle, Info, Sparkles, Clock, Zap, Globe, Server, Search, Radio, ExternalLink, Terminal, CheckCircle2, XCircle } from "lucide-react";
import { cn } from "@/lib/util";

// ----------------------------------------------------------------------------
//  Nuclei debug panel — surfaces which templates were loaded/executed so the
//  operator can confirm the community set is running, not a minimal subset.
// ----------------------------------------------------------------------------
function NucleiDebugPanel({ debug }) {
  if (!debug) return null;
  const loaded = debug.templates_loaded;
  const executed = debug.templates_executed;
  const onDisk = debug.templates_on_disk;
  const clustered = debug.templates_clustered;
  // "Community set OK" = we resolved a templates dir with a healthy number of
  // yaml files (>500) and Nuclei reported >100 templates loaded. Anything
  // lower likely means we're on a minimal / dev subset.
  const communityOk = debug.templates_dir_exists
    && onDisk >= 500
    && (loaded === undefined || loaded >= 100);
  return (
    <section className="border border-primary/40 bg-primary/5 p-4 space-y-3"
             data-testid="nuclei-debug-panel">
      <div className="flex items-center gap-2">
        <Terminal className="w-4 h-4 text-primary" />
        <div>
          <div className="overline text-primary">Nuclei · debug / verbose</div>
          <div className="font-display text-sm">Templates cargados y ejecutados</div>
        </div>
      </div>

      <div className={cn(
        "border p-2 text-xs font-mono flex items-center gap-2",
        communityOk
          ? "border-sev-low/40 bg-sev-low/5 text-sev-low"
          : "border-sev-medium/40 bg-sev-medium/5 text-sev-medium"
      )} data-testid="nuclei-community-check">
        {communityOk ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
        <span>
          {communityOk
            ? `Set community activo · ${onDisk.toLocaleString()} templates en disco, ${loaded ?? "?"} cargados en esta corrida.`
            : `Advertencia: parece un subconjunto mínimo (${onDisk || 0} templates en disco, ${loaded ?? "?"} cargados).`}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <DebugStat label="Nuclei" value={debug.nuclei_version || "—"} />
        <DebugStat label="Templates ver." value={debug.templates_version || "—"} />
        <DebugStat label="En disco" value={onDisk?.toLocaleString?.() ?? onDisk ?? "—"} />
        <DebugStat label="Cargados" value={loaded ?? "—"} />
        <DebugStat label="Ejecutados" value={executed ?? loaded ?? "—"} accent />
        <DebugStat label="Clusterizados" value={clustered ?? "—"} />
        <DebugStat label="Hits" value={debug.findings_count ?? 0} accent />
        <DebugStat label="Return code" value={debug.rc ?? "—"} />
      </div>

      <div className="text-[10px] font-mono text-muted-foreground space-y-0.5">
        <div>
          <span className="text-foreground">Severidad:</span> {debug.severity_filter || "—"}
          {" · "}
          <span className="text-foreground">Tags excluidos:</span> {(debug.excluded_tags || []).join(", ") || "—"}
        </div>
        <div className="truncate" title={debug.templates_dir}>
          <span className="text-foreground">Templates dir:</span> {debug.templates_dir || "—"}
          {" · "}
          {debug.templates_dir_exists
            ? <span className="text-sev-low">existe</span>
            : <span className="text-sev-critical">no existe</span>}
        </div>
      </div>

      {debug.command && (
        <details data-testid="nuclei-debug-command">
          <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground">
            Ver comando completo
          </summary>
          <pre className="mt-1 text-[10px] font-mono border border-border p-2 bg-background overflow-x-auto">
{debug.command}
          </pre>
        </details>
      )}

      {debug.log && (
        <details data-testid="nuclei-debug-log">
          <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground">
            Ver log verbose (banner + progreso)
          </summary>
          <pre className="mt-1 text-[10px] font-mono border border-border p-2 bg-background overflow-x-auto max-h-72">
{debug.log}
          </pre>
        </details>
      )}
    </section>
  );
}

function DebugStat({ label, value, accent }) {
  return (
    <div className="border border-border p-2 bg-background/60">
      <div className="overline text-[9px]">{label}</div>
      <div className={cn("font-display text-lg leading-tight",
                         accent ? "text-primary" : "text-foreground")}>
        {value}
      </div>
    </div>
  );
}

/**
 * Presentación visual de un resultado de escaneo.
 *
 * Reemplaza el JSON crudo por:
 *  · Resumen ejecutivo (badge + score + veredicto en lenguaje simple)
 *  · Cards de meta específicas por herramienta (puertos, subdomains, verdict…)
 *  · Findings con íconos, colores y descripciones humanas
 */

const SEVERITY_META = {
  critical: {
    label: "CRÍTICO",
    icon: AlertOctagon,
    tone: "text-sev-critical",
    bg: "bg-sev-critical/10",
    border: "border-sev-critical/50",
    pill: "bg-sev-critical text-white",
    plainLabel: "Acción inmediata",
  },
  high: {
    label: "ALTO",
    icon: AlertTriangle,
    tone: "text-sev-critical",
    bg: "bg-sev-critical/10",
    border: "border-sev-critical/40",
    pill: "bg-sev-critical/90 text-white",
    plainLabel: "Riesgo elevado",
  },
  medium: {
    label: "MEDIO",
    icon: ShieldAlert,
    tone: "text-sev-medium",
    bg: "bg-sev-medium/10",
    border: "border-sev-medium/40",
    pill: "bg-sev-medium text-black",
    plainLabel: "Requiere atención",
  },
  low: {
    label: "BAJO",
    icon: Info,
    tone: "text-sev-low",
    bg: "bg-sev-low/10",
    border: "border-sev-low/40",
    pill: "bg-sev-low/20 text-sev-low",
    plainLabel: "Informativo",
  },
  clean: {
    label: "LIMPIO",
    icon: ShieldCheck,
    tone: "text-sev-low",
    bg: "bg-sev-low/10",
    border: "border-sev-low/40",
    pill: "bg-sev-low/20 text-sev-low",
    plainLabel: "Sin hallazgos",
  },
};

const CATEGORY_LABELS = {
  threat_intel:              "Reputación / Threat Intel",
  exposed_services:          "Servicios expuestos",
  attack_surface_hygiene:    "Higiene de la superficie",
  vulnerability:             "Vulnerabilidad",
  credential_exposure:       "Filtración de credenciales",
  tls_certificates:          "TLS / Certificados",
  email_security:            "Seguridad de correo",
  web_configuration:         "Configuración web",
};

/**
 * Calcula el veredicto general del scan a partir de los findings.
 * Devuelve el nivel más severo detectado. Si no hay findings → "clean".
 */
export function computeVerdict(findings = []) {
  const order = ["critical", "high", "medium", "low"];
  for (const sev of order) {
    if (findings.some(f => f.severity === sev)) return sev;
  }
  return "clean";
}

/**
 * Genera una frase en lenguaje simple describiendo el estado del target.
 */
function verdictPhrase(job, verdict, findings) {
  const target = job.target || "el objetivo";
  const skipped = job.meta?.skipped;
  if (skipped) {
    return `Este módulo se omitió (${job.meta?.reason || "sin API key"}). Configúralo en el backend para activarlo.`;
  }
  if (job.status === "error" || job.error) {
    return `El escaneo falló antes de completarse. Revisa el detalle técnico más abajo.`;
  }
  const n = findings.length;
  switch (verdict) {
    case "critical":
      return `⚠ ${target} presenta ${n} hallazgo${n === 1 ? "" : "s"} — al menos uno es CRÍTICO y requiere respuesta inmediata.`;
    case "high":
      return `${target} tiene ${n} hallazgo${n === 1 ? "" : "s"} de riesgo elevado. Prioriza su remediación en las próximas 48 h.`;
    case "medium":
      return `${target} presenta ${n} hallazgo${n === 1 ? "" : "s"} moderado${n === 1 ? "" : "s"}. Planifica su corrección en el siguiente sprint.`;
    case "low":
      return `${target} muestra ${n} punto${n === 1 ? "" : "s"} de mejora leve${n === 1 ? "" : "s"}. Bajo riesgo, útil para higiene general.`;
    default:
      return `${target} está limpio. No se detectaron problemas relevantes en esta corrida.`;
  }
}

// ----------------------------------------------------------------------------
//  Meta cards específicas por herramienta
// ----------------------------------------------------------------------------
function MetaCard({ icon: Icon, label, value, hint, tone = "text-foreground" }) {
  return (
    <div className="border border-border p-3 bg-card/40" data-testid={`meta-card-${label.toLowerCase().replace(/\W+/g,'-')}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className={cn("w-3 h-3", tone)} />
        <span className="overline text-[10px]">{label}</span>
      </div>
      <div className={cn("font-display text-2xl font-medium", tone)}>{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

/**
 * Extrae 2-4 "highlights" del `meta` según el tool, listos para renderizar.
 */
function extractHighlights(job) {
  const m = job.meta || {};
  const tool = job.tool_id;
  const out = [];
  // AbuseIPDB
  if (m.abuse_confidence !== undefined) {
    const conf = m.abuse_confidence;
    out.push({ icon: ShieldAlert, label: "Confianza de abuso", value: `${conf}%`,
               hint: conf >= 75 ? "IP maliciosa" : conf >= 25 ? "IP sospechosa" : "IP limpia",
               tone: conf >= 75 ? "text-sev-critical" : conf >= 25 ? "text-sev-medium" : "text-sev-low" });
    out.push({ icon: Radio, label: "Reports", value: m.total_reports || 0,
               hint: `ISP: ${m.isp || "—"}` });
    if (m.country) out.push({ icon: Globe, label: "País", value: m.country });
  }
  // VirusTotal
  if (m.stats) {
    const s = m.stats;
    const mal = s.malicious || 0;
    out.push({ icon: AlertOctagon, label: "Motores maliciosos", value: mal,
               tone: mal >= 5 ? "text-sev-critical" : mal >= 1 ? "text-sev-medium" : "text-sev-low",
               hint: `${s.suspicious || 0} sospechosos · ${s.harmless || 0} limpios` });
    if (m.reputation !== undefined) {
      out.push({ icon: Sparkles, label: "Reputación agregada", value: m.reputation ?? "—",
                 tone: (m.reputation || 0) < 0 ? "text-sev-critical" : "text-sev-low" });
    }
  }
  // Shodan
  if (m.ports && Array.isArray(m.ports)) {
    out.push({ icon: Server, label: "Puertos abiertos", value: m.ports.length,
               hint: m.ports.slice(0, 8).join(", ") + (m.ports.length > 8 ? "…" : "") });
    if (m.vulns_count !== undefined) {
      out.push({ icon: AlertOctagon, label: "CVEs reportados", value: m.vulns_count,
                 tone: m.vulns_count > 0 ? "text-sev-critical" : "text-sev-low" });
    }
    if (m.org) out.push({ icon: Globe, label: "Organización", value: m.org });
  }
  // GreyNoise
  if (m.classification) {
    const cls = m.classification;
    out.push({ icon: Radio, label: "Clasificación", value: cls.toUpperCase(),
               tone: cls === "malicious" ? "text-sev-critical" : cls === "suspicious" ? "text-sev-medium" : "text-sev-low",
               hint: m.name || "" });
  }
  // SecurityTrails
  if (m.subdomain_count !== undefined) {
    out.push({ icon: Search, label: "Subdominios", value: m.subdomain_count,
               tone: m.subdomain_count > 100 ? "text-sev-medium" : "text-foreground",
               hint: m.subdomain_count > 100 ? "Superficie amplia" : "Superficie manejable" });
    if (m.dns_history) {
      out.push({ icon: Clock, label: "Historial DNS", value: m.dns_history.length || 0,
                 hint: "cambios recientes" });
    }
  }
  // URLScan
  if (m.malicious !== undefined) {
    out.push({ icon: m.malicious ? AlertOctagon : ShieldCheck,
               label: "Veredicto URLScan", value: m.malicious ? "MALICIOSA" : "LIMPIA",
               tone: m.malicious ? "text-sev-critical" : "text-sev-low",
               hint: `score: ${m.score || 0}/100` });
  }
  // HIBP
  if (m.addresses_exposed !== undefined) {
    const n = m.addresses_exposed;
    out.push({ icon: AlertTriangle, label: "Correos filtrados", value: n,
               tone: n >= 50 ? "text-sev-critical" : n >= 10 ? "text-sev-critical" : n > 0 ? "text-sev-medium" : "text-sev-low",
               hint: `${m.breach_count || 0} brechas` });
  }
  // Censys hosts
  if (Array.isArray(m.services)) {
    out.push({ icon: Server, label: "Servicios detectados", value: m.services.length });
  }
  // Nmap / Naabu open ports
  if (Array.isArray(m.open_ports)) {
    out.push({ icon: Server, label: "Puertos abiertos", value: m.open_ports.length,
               hint: m.open_ports.slice(0, 6).map(p => p.port || p).join(", ") });
  }
  // testssl grade
  if (m.grade) {
    const bad = ["F", "T"].includes(m.grade);
    out.push({ icon: ShieldCheck, label: "Grade TLS", value: m.grade,
               tone: bad ? "text-sev-critical" : m.grade === "A" ? "text-sev-low" : "text-sev-medium" });
  }
  // Nuclei findings by severity
  if (m.severity_counts) {
    const sc = m.severity_counts;
    out.push({ icon: AlertOctagon, label: "Nuclei críticos+altos",
               value: (sc.critical || 0) + (sc.high || 0),
               tone: ((sc.critical || 0) + (sc.high || 0)) > 0 ? "text-sev-critical" : "text-sev-low" });
  }
  // Subfinder / Amass count
  if (m.count !== undefined && (tool === "amass" || tool === "subfinder" || tool === "theharvester")) {
    out.push({ icon: Search, label: "Hosts descubiertos", value: m.count });
  }
  // theHarvester emails
  if (m.email_count !== undefined) {
    out.push({ icon: AlertTriangle, label: "Correos OSINT", value: m.email_count });
  }
  return out.slice(0, 4);
}

// ----------------------------------------------------------------------------
//  Componente principal
// ----------------------------------------------------------------------------
export default function ScanReportCard({ job }) {
  const findings = job.findings || [];
  const skipped = job.meta?.skipped;
  const verdict = skipped ? "clean" : computeVerdict(findings);
  const sev = SEVERITY_META[verdict];
  const Icon = sev.icon;
  const highlights = extractHighlights(job);
  const phrase = verdictPhrase(job, verdict, findings);

  return (
    <div className="space-y-5" data-testid="scan-report-card">
      {/* ============ EXECUTIVE SUMMARY ============ */}
      <div className={cn("border p-5", sev.border, sev.bg)} data-testid="scan-executive-summary">
        <div className="flex items-start gap-4">
          <div className={cn("shrink-0 p-3 border rounded", sev.border, "bg-background/60")}>
            <Icon className={cn("w-8 h-8", sev.tone)} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="overline text-[10px]">Resumen ejecutivo</span>
              <span className={cn("px-2 py-0.5 text-[10px] font-mono tracking-widest uppercase font-bold rounded",
                                   sev.pill)}
                    data-testid="verdict-badge">
                {sev.label}
              </span>
              <span className="text-[10px] text-muted-foreground font-mono">
                {sev.plainLabel}
              </span>
            </div>
            <p className="text-sm leading-relaxed" data-testid="verdict-phrase">{phrase}</p>
            <div className="mt-3 flex flex-wrap gap-4 text-[11px] text-muted-foreground font-mono">
              <span><b className="text-foreground">{findings.length}</b> hallazgo{findings.length === 1 ? "" : "s"}</span>
              <span>·</span>
              <span>Objetivo: <b className="text-foreground truncate max-w-[200px] inline-block align-bottom">{job.target}</b></span>
              {job.finished_at && <>
                <span>·</span>
                <span>Finalizado {new Date(job.finished_at).toLocaleString()}</span>
              </>}
            </div>
          </div>
        </div>
      </div>

      {/* ============ META HIGHLIGHTS ============ */}
      {highlights.length > 0 && (
        <div>
          <div className="overline mb-2">Métricas clave</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="meta-highlights">
            {highlights.map((h, i) => <MetaCard key={i} {...h} />)}
          </div>
        </div>
      )}

      {/* ============ NUCLEI DEBUG (verbose mode) ============ */}
      {job.tool_id === "nuclei" && job.meta?.debug && (
        <NucleiDebugPanel debug={job.meta.debug} />
      )}

      {/* ============ FINDINGS CARDS ============ */}
      {findings.length > 0 && (
        <div>
          <div className="overline mb-2">Hallazgos detallados ({findings.length})</div>
          <div className="space-y-2" data-testid="findings-cards">
            {findings.map((f) => <FindingCard key={f.id} finding={f} />)}
          </div>
        </div>
      )}

      {/* ============ EMPTY STATE (LIMPIO) ============ */}
      {findings.length === 0 && !skipped && job.status === "done" && (
        <div className="border border-sev-low/40 bg-sev-low/5 p-6 text-center" data-testid="clean-state">
          <ShieldCheck className="w-10 h-10 text-sev-low mx-auto mb-2" />
          <div className="font-display text-lg text-sev-low">Sin hallazgos</div>
          <div className="text-xs text-muted-foreground mt-1">
            El análisis se completó sin detectar problemas de seguridad.
          </div>
        </div>
      )}

      {/* ============ ERROR STATE ============ */}
      {(job.status === "error" || job.error) && (
        <div className="border border-sev-critical/40 bg-sev-critical/5 p-4" data-testid="error-state">
          <div className="flex items-center gap-2 text-sev-critical overline mb-1">
            <AlertOctagon className="w-3 h-3" /> Error de ejecución
          </div>
          <div className="text-xs font-mono text-muted-foreground">{job.error || "sin detalle"}</div>
        </div>
      )}

      {/* ============ RAW DATA (colapsable) ============ */}
      {(job.raw_preview || (job.meta && Object.keys(job.meta).length > 0)) && (
        <details className="border border-border" data-testid="raw-details">
          <summary className="p-3 text-xs font-mono tracking-widest uppercase cursor-pointer hover:bg-muted/40 text-muted-foreground">
            Ver salida técnica (JSON crudo + meta completo)
          </summary>
          <div className="p-3 space-y-3 border-t border-border">
            {job.meta && Object.keys(job.meta).length > 0 && (
              <div>
                <div className="overline mb-1">Meta completo</div>
                <pre className="text-[10px] font-mono border border-border p-2 bg-background overflow-x-auto max-h-56">
{JSON.stringify(job.meta, null, 2)}
                </pre>
              </div>
            )}
            {job.raw_preview && (
              <div>
                <div className="overline mb-1">Salida cruda</div>
                <pre className="text-[10px] font-mono border border-border p-2 bg-background overflow-x-auto max-h-64">
{job.raw_preview}
                </pre>
              </div>
            )}
          </div>
        </details>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
function FindingCard({ finding: f }) {
  const sev = SEVERITY_META[f.severity] || SEVERITY_META.low;
  const Icon = sev.icon;
  const category = CATEGORY_LABELS[f.category] || f.category;
  return (
    <div className={cn("border p-3 flex items-start gap-3", sev.border, sev.bg)}
         data-testid={`finding-card-${f.id}`}>
      <Icon className={cn("w-5 h-5 shrink-0 mt-0.5", sev.tone)} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className={cn("px-1.5 py-0.5 text-[9px] font-mono tracking-widest uppercase font-bold rounded",
                               sev.pill)}>
            {sev.label}
          </span>
          <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">
            {category}
          </span>
          {f.cve && (
            <a href={`https://nvd.nist.gov/vuln/detail/${f.cve}`}
               target="_blank" rel="noopener noreferrer"
               className="text-[10px] font-mono text-primary hover:underline inline-flex items-center gap-1">
              {f.cve} <ExternalLink className="w-2.5 h-2.5" />
            </a>
          )}
        </div>
        <div className="font-medium text-sm mb-1">{f.title}</div>
        <div className="text-xs text-muted-foreground leading-relaxed">{f.description}</div>
        <div className="mt-2 flex items-center gap-3 text-[10px] font-mono text-muted-foreground">
          <span>Activo: <b className="text-foreground">{f.asset_label}</b></span>
          {f.external_ref && (
            <>
              <span>·</span>
              <a href={f.external_ref} target="_blank" rel="noopener noreferrer"
                 className="text-primary hover:underline inline-flex items-center gap-1">
                Referencia externa <ExternalLink className="w-2.5 h-2.5" />
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
